import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Link } from "wouter";
import type { DataProgress } from "../../shared/sync";
import { Box, ErrorNotice, Notice, Spinner } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { count, timeAgo } from "../../web/lib/format";
import { useMoney } from "../../web/lib/money";
import { PeriodPicker, usePeriod } from "../../web/components/PeriodPicker";
import { addDays, type BarUnit, barUnit, daysIn, dayText, type Period, periodText, todayIn } from "../../shared/periods";
import { can, useGroupStats, useItemStats, useSupplierStats } from "./data";
import { activeJobs, type BarUsage, type GroupTime, type ItemStats, type ItemTime, type JobData, meta, type PriceListData, type Usage } from "./meta";
import { duration } from "./describe";

/*
 * Stats tabs for items, suppliers and groups (stats.ts): how they're used on jobs over the
 * period chosen (the last 12 months unless another is: PeriodPicker). They need the optional Jobs and Job lines data: turned off, the tab says to turn
 * them on in Settings › ServiceM8 (managers; others are told to ask one); still being read, it says
 * how far back.
 */

/** A window's tabs, without Stats for someone whose role can't see stats. */
export const withStatsTab = <T extends { id: string }>(tabs: readonly T[], data: Pick<PriceListData, "whyNot">): T[] => tabs.filter((t) => t.id !== "stats" || can(data, "stats"));

/** "2026-03-14" as a date ("14/03/2026"), or "-". */
const day = (d: string | null) => (d ? dayText(d) : "-");

/** How long ago a day was, from `today` (the business's): "today", "yesterday", "12 days ago", "5 months ago", "2 years ago". */
function daysAgo(d: string, today: string) {
    const days = daysIn({ from: d, to: today }) - 1;
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 60) return `${days} days ago`;
    const months = Math.round(days / 30.4);
    if (months < 24) return `${months} months ago`;
    return `${Math.round(days / 365)} years ago`;
}

// ---------------------------------------------------------------------------------------------
// Whether jobs and job lines are there
// ---------------------------------------------------------------------------------------------

const NAMES = { jobs: "Jobs", lines: "Job lines" } as const;

/** Which of jobs and job lines are in this state ("Jobs and Job lines"). */
const which = (data: JobData, test: (p: DataProgress) => boolean) => (["jobs", "lines"] as const).filter((k) => test(data[k]));

/** Jobs and job lines can't be used yet (off, or not allowed): the stats that need them aren't shown. */
export const jobDataMissing = (data: JobData) => which(data, (p) => p.readiness === "off" || p.readiness === "no-permission").length > 0;

/**
 * Why stats from jobs are missing or partial, and what to do: turn them on in Settings (managers;
 * others are told to ask one), reconnect to allow them, or wait while the history is read (how far
 * back so far). Nothing when ready.
 */
function JobDataNotice({ data }: Readonly<{ data: JobData }>) {
    const company = useCompany();
    const off = which(data, (p) => p.readiness === "off");
    const settings = <Link href="/settings/servicem8">Settings › ServiceM8</Link>;
    if (off.length) {
        const named = off.map((k) => NAMES[k]).join(" and ");
        return (
            <Notice>
                Stats come from the items on your jobs. {company.canManage ? <>To see them, turn on {named} in {settings}.</> : <>To see them, ask a manager to turn on {named} in {settings}.</>}{" "}
                Reading your job history can take a while; the figures fill in as it does.
            </Notice>
        );
    }
    if (which(data, (p) => p.readiness === "no-permission").length) {
        return <Notice tone="warning">Stats come from the items on your jobs: reconnect ServiceM8 ({settings}) and allow reading jobs and job lines.</Notice>;
    }
    const loading = which(data, (p) => p.readiness === "loading");
    if (loading.length) {
        const back = loading
            .map((k) => data[k].reachedBack)
            .filter((d): d is string => d !== null)
            .sort()
            .at(-1);
        return (
            <Notice>
                Still reading your job history from ServiceM8{back ? `: back to ${day(back)} so far` : ""}. These figures will grow as it does.
            </Notice>
        );
    }
    return null;
}

// ---------------------------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------------------------

/** What its active jobs are: "1 not yet approved, 1 quote" (or "None" with neither). */
function activeBreakdown(s: Pick<ItemStats, "openJobs" | "openQuotes">) {
    const parts = [s.openJobs ? `${count(s.openJobs)} not yet approved` : "", s.openQuotes ? `${count(s.openQuotes)} ${s.openQuotes === 1 ? "quote" : "quotes"}` : ""].filter(Boolean);
    return parts.length ? parts.join(", ") : "None";
}

/** A figure: its label, the value large, and a line under it. */
function Figure({ label, value, sub }: Readonly<{ label: string; value: ReactNode; sub?: ReactNode }>) {
    return (
        <div className="min-w-0">
            <dt className="text-xs text-muted">{label}</dt>
            <dd className="truncate text-lg font-semibold tabular-nums">{value}</dd>
            {sub ? <dd className="truncate text-xs text-muted">{sub}</dd> : null}
        </div>
    );
}

function Figures({ children }: Readonly<{ children: ReactNode }>) {
    return <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">{children}</dl>;
}

/** Profit as a share of sales ("32% margin"), or nothing without sales. */
function useMargin() {
    const fmt = useMoney();
    return (u: Usage) => (u.sales > 0 ? `${fmt.percent(Math.round(((u.sales - u.cost) / u.sales) * 100))} margin` : undefined);
}

/** How much was used through the period, as bars: by day, week or month (hover for the figures). */
function UsageBars({ bars, period }: Readonly<{ bars: BarUsage[]; period: Period }>) {
    const fmt = useMoney();
    const { money } = useCompany();
    const locale = money.locale ?? undefined;
    const unit = barUnit(period);
    const most = Math.max(...bars.map((b) => b.quantity), 0);
    // Every label when they fit, else every few (a month's days).
    const every = bars.length <= 12 ? 1 : Math.ceil(bars.length / 10);
    const nameOf = (b: BarUsage, i: number) => {
        if (unit === "month") return dayText(b.from, locale, { month: "long", year: "numeric" });
        if (unit === "day") return dayText(b.from, locale, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
        const next = bars[i + 1];
        return periodText({ from: b.from, to: next ? addDays(next.from, -1) : period.to }, locale);
    };
    // Months with their year when there are more than 12 ("Jan 26").
    const monthLabel: Intl.DateTimeFormatOptions = bars.length > 12 ? { month: "short", year: "2-digit" } : { month: "short" };
    const labelFormat: Record<BarUnit, Intl.DateTimeFormatOptions> = { month: monthLabel, day: { day: "numeric" }, week: { day: "numeric", month: "short" } };
    const shortOf = (b: BarUsage) => dayText(b.from, locale, labelFormat[unit]);
    return (
        <div>
            <div className="flex h-28 items-end gap-1" role="img" aria-label={`How many were used each ${unit}, ${periodText(period, locale)}`}>
                {bars.map((b, i) => (
                    <div key={b.from} className="flex h-full flex-1 items-end" title={`${nameOf(b, i)}: ${fmt.number(b.quantity)} used, ${fmt.money(b.sales)}`}>
                        <div className="w-full rounded-t bg-brand-500" style={{ height: most > 0 ? `${Math.max((b.quantity / most) * 100, b.quantity > 0 ? 4 : 0)}%` : 0 }} />
                    </div>
                ))}
            </div>
            <div className="mt-1 flex gap-1 border-t border-line pt-1">
                {bars.map((b, i) => (
                    // A label can run into the blank ones beside it (every few bars have one).
                    <span key={b.from} className="flex min-w-0 flex-1 justify-center overflow-visible text-[11px] whitespace-nowrap text-muted">
                        {i % every === 0 ? shortOf(b) : ""}
                    </span>
                ))}
            </div>
        </div>
    );
}

/**
 * The period picker over a tab's figures (which are all for it, so their titles don't repeat it),
 * with a ready-made period's days beside it ("1 Oct - 31 Dec 2026"; a custom one shows them on the
 * button). It stays while the figures for a new period load.
 */
function PeriodBar({ value }: Readonly<{ value: ReturnType<typeof usePeriod> }>) {
    const { money } = useCompany();
    return (
        <Box>
            <div className="flex items-center gap-3">
                <PeriodPicker value={value} />
                {"preset" in value.choice ? <span className="min-w-0 truncate text-xs text-muted tabular-nums">{periodText(value.period, money.locale)}</span> : null}
            </div>
        </Box>
    );
}

/** The stats once loaded: a spinner, an error, or `children` with the data. */
function Loaded<T>({ query, children }: Readonly<{ query: { data?: T; error: Error | null; isLoading: boolean }; children: (data: T) => ReactNode }>) {
    if (query.isLoading) return <Spinner />;
    if (!query.data) return <ErrorNotice error={query.error ?? new Error("The stats couldn't be loaded.")} />;
    return <>{children(query.data)}</>;
}

// ---------------------------------------------------------------------------------------------
// The tabs
// ---------------------------------------------------------------------------------------------

/** An item's Stats tab: how much it's used on jobs, quoted, when last, and on jobs not yet approved. */
export function ItemStatsTab({ uuid }: Readonly<{ uuid: string }>) {
    const period = usePeriod();
    return (
        <div className="space-y-4">
            <PeriodBar value={period} />
            <Loaded query={useItemStats(uuid, period.period)}>
                {({ data, stats }) => (
                    <div className="space-y-4">
                        <JobDataNotice data={data} />
                        {jobDataMissing(data) ? null : <ItemFigures stats={stats} period={period.period} />}
                        {jobDataMissing(data) ? null : <ItemTimeBox time={stats.time} progress={data.times} />}
                    </div>
                )}
            </Loaded>
        </div>
    );
}

/**
 * Profit is only as right as the costs recorded on the jobs: when some had none (the item had no
 * cost then), it says so. Nothing otherwise; it goes for a period without those jobs.
 */
function NoCostNote({ usage }: Readonly<{ usage: Usage }>) {
    if (!usage.noCostJobs) return null;
    return (
        <p className="text-xs text-muted">
            <Info className="mr-1 inline size-3.5 align-[-2px]" />
            {count(usage.noCostJobs)} of {count(usage.jobs)} {usage.jobs === 1 ? "job" : "jobs"} had no cost recorded: profit may be high.
        </p>
    );
}

function ItemFigures({ stats, period }: Readonly<{ stats: ItemStats; period: Period }>) {
    const fmt = useMoney();
    const { timeZone } = useCompany();
    const margin = useMargin();
    const year = stats.period;
    return (
        <>
            <Box title="On jobs">
                <Figures>
                    <Figure label="Used" value={fmt.number(year.quantity)} sub={`on ${count(year.jobs)} ${year.jobs === 1 ? "job" : "jobs"}`} />
                    <Figure label="Sales" value={fmt.money(year.sales)} sub="before tax" />
                    <Figure label="Profit" value={fmt.money(year.sales - year.cost)} sub={margin(year)} />
                    <Figure label="Quoted" value={fmt.number(stats.quotes.quantity)} sub={`on ${count(stats.quotes.quotes)} ${stats.quotes.quotes === 1 ? "quote" : "quotes"}`} />
                </Figures>
                <NoCostNote usage={year} />
                <UsageBars bars={stats.bars} period={period} />
            </Box>
            <Box>
                <Figures>
                    <Figure label="Last used" value={day(stats.lastUsed)} sub={stats.lastUsed ? daysAgo(stats.lastUsed, todayIn(timeZone)) : "Not on any job"} />
                    <Figure label="Active jobs" value={count(activeJobs(stats))} sub={activeBreakdown(stats)} />
                    <Figure label="Used, all time" value={fmt.number(stats.allTime.quantity)} sub={`on ${count(stats.allTime.jobs)} ${stats.allTime.jobs === 1 ? "job" : "jobs"}`} />
                    <Figure label="Sales, all time" value={fmt.money(stats.allTime.sales)} sub="before tax" />
                </Figures>
                <p className="truncate text-xs text-muted" title="Quotes are counted on their own (Quoted), and unsuccessful jobs not at all.">
                    From work orders and completed jobs, at the prices charged then.
                </p>
            </Box>
            <ActiveJobs stats={stats} />
        </>
    );
}

/** Minutes, to the minute ("25 min", "1 hr 5 min"). */
const mins = (n: number) => duration(Math.round(n));

/** Where job time is set up (Settings › Price List › Job time). */
const JobTimeSettingsLink = () => <Link href={`/settings/${meta.id}#job-time`}>Settings › Price List</Link>;

/**
 * "Time on jobs", for an item or a group: `children` once there's job time, else how to get it (turn it on, reconnect); while
 * it's still being read, how far back it's got.
 */
function TimeBox({ time, progress, children }: Readonly<{ time: object | null; progress: DataProgress; children: () => ReactNode }>) {
    const company = useCompany();
    const settings = <Link href="/settings/servicem8">Settings › ServiceM8</Link>;
    let body: ReactNode;
    if (progress.readiness === "off") {
        body = <p className="m-0 text-muted">{company.canManage ? <>To see how long it takes on jobs, turn on Job time in {settings}.</> : <>To see how long it takes on jobs, ask a manager to turn on Job time in {settings}.</>}</p>;
    } else if (progress.readiness === "no-permission" || !time) {
        body = <p className="m-0 text-muted">To see how long it takes on jobs, reconnect ServiceM8 ({settings}) and allow reading the schedule.</p>;
    } else {
        body = children();
    }
    return (
        <Box title="Time on jobs">
            {body}
            {progress.readiness === "loading" ? (
                <p className="m-0 text-xs text-muted">Still reading job time from ServiceM8{progress.reachedBack ? `: back to ${day(progress.reachedBack)} so far` : ""}.</p>
            ) : null}
        </Box>
    );
}

/** How a job's time is counted, as set (Price List settings › Job time). */
function HowCounted({ time, what }: Readonly<{ time: { countDrive: boolean; lookBackMonths: number; everyJob: number | null }; what: ReactNode }>) {
    return (
        <p className="m-0 text-xs text-muted">
            {time.everyJob === null ? null : <>On top of the {mins(time.everyJob)} base time per visit. </>}
            Each completed job&apos;s checked-in time ({time.countDrive ? "drive counted" : "drive taken off"}, the last {count(time.lookBackMonths)} months), shared out between its items{what}. Change how in{" "}
            <JobTimeSettingsLink />.
        </p>
    );
}

/** How long it takes on jobs (jobTime.ts), whatever the period: the first of it on a job, and each extra one. */
function ItemTimeBox({ time, progress }: Readonly<{ time: ItemTime | null; progress: DataProgress }>) {
    return (
        <TimeBox time={time} progress={progress}>
            {() => {
                if (!time) return null;
                if (time.leftOut) {
                    return (
                        <p className="m-0 text-muted">
                            It&apos;s left out of job time (<JobTimeSettingsLink />
                            ), so it isn&apos;t timed.
                        </p>
                    );
                }
                if (time.first === null) {
                    return (
                        <p className="m-0 text-muted">
                            On {count(time.jobs)} counted {time.jobs === 1 ? "job" : "jobs"}: too few to time it on its own (it needs {count(time.minJobs)}).
                        </p>
                    );
                }
                return (
                    <>
                        <Figures>
                            <Figure label="First one" value={mins(time.first)} sub="on a job" />
                            <Figure label="Each extra" value={time.extra === null ? "-" : mins(time.extra)} sub={time.extra === null ? "too few jobs with more" : "on the same job"} />
                            <Figure label="From" value={count(time.jobs)} sub={time.jobs === 1 ? "job" : "jobs"} />
                        </Figures>
                        <HowCounted time={time} what="" />
                    </>
                );
            }}
        </TimeBox>
    );
}

/** How long a group's items take on jobs (jobTime.ts): its timed items' times together, each by how often it's used. */
function GroupTimeBox({ time, progress }: Readonly<{ time: GroupTime | null; progress: DataProgress }>) {
    return (
        <TimeBox time={time} progress={progress}>
            {() => {
                if (!time) return null;
                if (time.first === null) {
                    return (
                        <p className="m-0 text-muted">
                            None of its {count(time.items)} items {time.jobs ? `is on enough of its ${count(time.jobs)} counted jobs` : "is on a counted job"} to time it yet (each needs {count(time.minJobs)}).
                        </p>
                    );
                }
                const range = time.fastest !== null && time.slowest !== null ? `${mins(time.fastest)} – ${mins(time.slowest)}` : "-";
                return (
                    <>
                        <Figures>
                            <Figure label="First one" value={mins(time.first)} sub="on a job, usually" />
                            <Figure label="Each extra" value={time.extra === null ? "-" : mins(time.extra)} sub={time.extra === null ? "too few jobs with more" : "on the same job"} />
                            <Figure label="Its items" value={range} sub="fastest to slowest" />
                            <Figure label="From" value={count(time.jobs)} sub={`jobs, ${count(time.timed)} of ${count(time.items)} items timed`} />
                        </Figures>
                        <HowCounted time={time} what=": its timed items' together, each counted by how often it's used" />
                    </>
                );
            }}
        </TimeBox>
    );
}

/** A job in ServiceM8 (its web app; ServiceM8's own link for a job, from its API docs). */
const jobLink = (uuid: string) => `https://go.servicem8.com/OpenJob/${encodeURIComponent(uuid)}`;

/** The item's active jobs, newest first: the jobs a change to it could matter to (e.g. its item number). */
function ActiveJobs({ stats }: Readonly<{ stats: ItemStats }>) {
    const fmt = useMoney();
    const total = activeJobs(stats);
    const list = stats.activeList;
    return (
        <Box title="Active jobs">
            {list.length ? (
                <table className="list is-fixed">
                    <colgroup>
                        <col className="w-[18%]" />
                        <col />
                        <col className="w-[20%]" />
                        <col className="w-[14%]" />
                        <col className="w-[20%]" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Job</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th className="text-right">Qty</th>
                            <th className="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((j) => (
                            <tr key={j.uuid}>
                                <td className="font-medium">
                                    <a href={jobLink(j.uuid)} target="_blank" rel="noreferrer" className="text-brand-ink" title="Open the job in ServiceM8">
                                        {j.number ? `#${j.number}` : "Open"}
                                    </a>
                                </td>
                                <td className="truncate">{j.status === "Quote" ? "Quote" : `${j.status || "Job"}, not approved`}</td>
                                <td className="text-muted">{day(j.day || null)}</td>
                                <td className="text-right tabular-nums">{fmt.number(j.quantity)}</td>
                                <td className="text-right tabular-nums">{fmt.money(j.sales)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="text-sm text-muted">Not on any active jobs.</p>
            )}
            <p className="text-xs text-muted">
                Quotes, and work orders and completed jobs not yet approved. Amounts before tax, as on the job.
                {total > list.length ? ` The newest ${count(list.length)} of ${count(total)}.` : ""}
            </p>
        </Box>
    );
}

/** A supplier's Stats tab: their prices (always), and what's likely bought from them (with job data). */
export function SupplierStatsTab({ uuid }: Readonly<{ uuid: string }>) {
    const fmt = useMoney();
    const period = usePeriod();
    return (
        <div className="space-y-4">
            <PeriodBar value={period} />
            <Loaded query={useSupplierStats(uuid, period.period)}>
                {({ data, stats }) => (
                    <div className="space-y-4">
                        <Box title="Their prices">
                            <Figures>
                                <Figure label="Items" value={count(stats.items)} sub="with a price from them" />
                                <Figure label="Cheapest for" value={count(stats.cheapestFor)} sub={stats.items ? `of ${count(stats.items)}` : undefined} />
                                <Figure label="Cost comes from them" value={count(stats.costFollows)} sub={stats.costFollows === 1 ? "item" : "items"} />
                                <Figure label="Due for a recheck" value={count(stats.due)} sub={stats.lastChecked ? `last checked ${timeAgo(stats.lastChecked)}` : undefined} />
                            </Figures>
                        </Box>
                        <Box title="Price changes">
                            <Figures>
                                <Figure label="Changes" value={count(stats.changes.count)} />
                                <Figure
                                    label="Average change"
                                    value={stats.changes.averagePct === null ? "-" : `${stats.changes.averagePct > 0 ? "+" : ""}${fmt.percent(Math.round(stats.changes.averagePct * 10) / 10)}`}
                                />
                                <Figure
                                    label="Latest increase"
                                    value={stats.changes.lastIncrease ? `+${fmt.percent(Math.round(stats.changes.lastIncrease.pct * 10) / 10)}` : "-"}
                                    sub={stats.changes.lastIncrease ? `${stats.changes.lastIncrease.item}, ${timeAgo(stats.changes.lastIncrease.at)}` : undefined}
                                />
                            </Figures>
                        </Box>
                        <Box title="Bought from them (estimate)">
                            <JobDataNotice data={data} />
                            {stats.estimatedSpend === null ? null : (
                                <>
                                    <Figures>
                                        <Figure label="Estimated spend" value={fmt.money(stats.estimatedSpend)} sub="before tax" />
                                    </Figures>
                                    <p className="text-xs text-muted">
                                        The cost of items used on jobs whose cost comes from them. ServiceM8 doesn&apos;t record who you bought from, so this is a guide.
                                    </p>
                                </>
                            )}
                        </Box>
                    </div>
                )}
            </Loaded>
        </div>
    );
}

/** A group's Stats tab: how its items are used on jobs, the markup achieved, and its most used items. */
export function GroupStatsTab({ id, onOpenItem }: Readonly<{ id: string; onOpenItem: (uuid: string) => void }>) {
    const fmt = useMoney();
    const margin = useMargin();
    const period = usePeriod();
    return (
        <div className="space-y-4">
            <PeriodBar value={period} />
            <Loaded query={useGroupStats(id, period.period)}>
                {({ data, stats }) => (
                    <div className="space-y-4">
                        <JobDataNotice data={data} />
                        {jobDataMissing(data) ? null : (
                            <>
                                <Box title="Its items on jobs">
                                    <Figures>
                                        <Figure label="Used" value={fmt.number(stats.period.quantity)} sub={`on ${count(stats.period.jobs)} ${stats.period.jobs === 1 ? "job" : "jobs"}`} />
                                        <Figure label="Sales" value={fmt.money(stats.period.sales)} sub="before tax" />
                                        <Figure label="Profit" value={fmt.money(stats.period.sales - stats.period.cost)} sub={margin(stats.period)} />
                                        <Figure label="Markup achieved" value={stats.markupPct === null ? "-" : fmt.percent(Math.round(stats.markupPct))} sub="on cost, as charged" />
                                    </Figures>
                                    <NoCostNote usage={stats.period} />
                                </Box>
                                <Box title="Most used">
                                    {stats.top.length ? (
                                        <ul className="space-y-1.5">
                                            {stats.top.map((t) => (
                                                <li key={t.uuid} className="flex items-baseline justify-between gap-3">
                                                    <button type="button" className="link-button min-w-0 truncate text-left" onClick={() => onOpenItem(t.uuid)}>
                                                        {t.name}
                                                    </button>
                                                    <span className="shrink-0 text-sm text-muted tabular-nums">
                                                        {fmt.number(t.quantity)} used · {fmt.money(t.sales)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-muted">None of its {count(stats.items)} items were used on jobs in this period.</p>
                                    )}
                                </Box>
                                <GroupTimeBox time={stats.time} progress={data.times} />
                            </>
                        )}
                    </div>
                )}
            </Loaded>
        </div>
    );
}
